import { Fragment, useState } from "react";

import { css } from "@compiled/react";
import { FormattedMessage, useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { DataEventProps } from "@/libraries/wpl/foundations/src";
import { Play } from "@/libraries/wpl/foundations/src/icons/object/play";
import { YoutubeModal } from "@/patterns/modal/src";

const videoSectionStyle = css({
    background:
        "linear-gradient( 180deg, transparent 50%, #e6fcff 50%, #e6fcff 100% )",
    position: "relative"
});

const videoCardWrapperStyles = css({
    "padding": token("space.200"),
    "marginInlineStart": "auto",
    "marginInlineEnd": "auto",
    "display": "flex",
    "justifyContent": "center",
    "@media (min-width: 540px)": {
        width: "66.66666%"
    },
    [media.above.sm]: {
        width: "83.33333%"
    },
    [media.above.md]: {
        width: "66.66666%"
    }
});

const videoCardContainerStyles = css({
    display: "flex",
    flexDirection: "column",
    boxShadow: "0px 4px 4px 0px rgba(0, 0, 0, 0.25)",
    backgroundColor: "#FAFBFC",
    borderRadius: "8px",
    maxWidth: "767.99px",
    overflow: "hidden",
    [media.above.sm]: {
        flexDirection: "row"
    }
});

const videoCardImageStyles = css({
    "position": "relative",
    "padding": `0 ${token("space.200")}`,
    "overflow": "hidden",
    [media.above.sm]: {
        flex: 5
    },
    [media.above.md]: {
        height: "184px",
        paddingInlineStart: "28px",
        paddingInlineEnd: "28px"
    },
    ":hover::before": {
        backgroundColor: "#ffffff",
        transform: "scale(1.06)"
    },
    "::before": {
        position: "absolute",
        content: '""',
        insetBlockStart: 0,
        insetInlineStart: 0,
        width: "100%",
        height: "100%",
        margin: 0,
        backgroundPosition: "center center",
        backgroundSize: "cover",
        backgroundColor: "rgba(23, 43, 77, 0.15)",
        backgroundBlendMode: "multiply",
        transformOrigin: "center center",
        transform: "scale(1)",
        transition: "all 1.3s",
        backgroundImage:
            "url(https://wac-cdn-2.atlassian.com/image/upload/f_auto,q_auto/dam/jcr:83995a77-0155-4484-b349-39c9501f3313/Confluence%20PT%20thumbnail@2x.png?cdnVersion=1263)",
        zIndex: 1
    },
    "img": {
        maxHeight: "340px",
        opacity: 0,
        width: "100%"
    },
    "svg": {
        zIndex: 2,
        position: "absolute",
        insetBlockStart: 0,
        insetInlineEnd: 0,
        insetInlineStart: 0,
        insetBlockEnd: 0,
        margin: "auto",
        fill: "rgba(255, 255, 255, 0.9)"
    }
});

const videoCardTextStyles = css({
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "stretch",
    padding: token("space.400"),
    h3: {
        color: "#253858",
        marginBlockStart: 0,
        fontSize: token("space.250"),
        letterSpacing: "0.3px",
        lineHeight: 1.2,
        marginBlockEnd: "14px",
        fontWeight: 500
    },
    [media.above.sm]: {
        flex: 7,
        h3: {
            fontSize: token("space.300")
        }
    },
    [media.above.md]: {
        padding: token("space.500"),
        h3: {
            lineHeight: token("space.400")
        }
    },
    a: {
        display: "flex",
        alignItems: "center",
        color: "#ffffff",
        span: {
            fontSize: token("space.200"),
            color: "#0052cc"
        },
        svg: {
            fill: "#0052cc",
            marginInlineEnd: token("space.100")
        }
    }
});

const VideoSection = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const intl = useIntl();

    const videoCardDataEventProps: DataEventProps = {
        "data-product-key": "confluence",
        "data-event-component": "linkButton",
        "data-event-container": "videoEmbedInline",
        "data-event": "clicked",
        "data-label": "watch the video_image"
    };

    const videoCardTextDataEventProps: DataEventProps = {
        "data-product-key": "confluence",
        "data-event-component": "linkButton",
        "data-event-container": "videoLink",
        "data-event": "clicked",
        "data-label-english": "Watch the video",
        "data-label": "Watch the video"
    };

    return (
        <Fragment>
            <div css={videoSectionStyle}>
                <div css={videoCardWrapperStyles}>
                    <div css={videoCardContainerStyles}>
                        <a
                            css={videoCardImageStyles}
                            href="https://www.youtube.com/watch?v=jqg8o-ataIQ"
                            {...videoCardDataEventProps}
                            onClick={(event) => {
                                event.preventDefault();
                                setIsModalOpen(true);
                            }}
                        >
                            <img
                                src="https://wac-cdn.atlassian.com/misc-assets/webp-images/Conf_ProductTour_Thumbnail.webp"
                                alt={intl.formatMessage({
                                    id: "ConfluenceSoftwarePageTemplate.Video.VideoThumbnailAltText",
                                    defaultMessage: "video thumbnail"
                                })}
                            />
                            <Play size="70" />
                        </a>
                        <div css={videoCardTextStyles}>
                            <h3>
                                <FormattedMessage
                                    id="ConfluenceSoftwarePageTemplate.Video.connects.VideoHeader"
                                    defaultMessage={
                                        "Learn how Confluence connects teams"
                                    }
                                />
                            </h3>
                            <a
                                css={css({
                                    ":hover": {
                                        "svg, span": {
                                            color: "#0065ff",
                                            fill: "#0065ff"
                                        }
                                    }
                                })}
                                href="https://www.youtube.com/watch?v=jqg8o-ataIQ"
                                {...videoCardTextDataEventProps}
                                onClick={(event) => {
                                    event.preventDefault();
                                    setIsModalOpen(true);
                                }}
                            >
                                <Play size="22" />
                                <span>
                                    <FormattedMessage
                                        id="ConfluenceSoftwarePageTemplate.Video.PlayVideo"
                                        defaultMessage={"Watch the video"}
                                    />
                                </span>
                            </a>
                        </div>
                    </div>
                </div>
            </div>

            <YoutubeModal
                isOpen={isModalOpen}
                src="https://www.youtube.com/embed/jqg8o-ataIQ?autoplay=1&rel=0&origin=https%3A%2F%2Fwww.atlassian.com&widget_referrer=https%3A%2F%2Fwww.atlassian.com&enablejsapi=1&widgetid=1"
                title={intl.formatMessage({
                    id: "ConfluenceSoftwarePageTemplate.Video.ModalYouTubeIframeTitle",
                    defaultMessage:
                        "Say hello to Confluence! | Confluence Overview | Atlassian"
                })}
                closeIconLabel={intl.formatMessage({
                    id: "ConfluenceSoftwarePageTemplate.Video.CloseModalButtonMobileLabel",
                    defaultMessage: "Close modal"
                })}
                closeModal={() => setIsModalOpen(false)}
            />
        </Fragment>
    );
};

export default VideoSection;
